import { ActionIcon } from '@mantine/core';
import { IconTrash } from '@tabler/icons-react';
import { AnimatePresence, motion } from 'motion/react';
import { useState } from 'react';

export const ExpandableImage = ({
	src,
	id,
	onDelete,
}: {
	src: string;
	id: string;
	onDelete?: () => void;
}) => {
	const [isExpanded, setIsExpanded] = useState(false);
	const [scale, setScale] = useState(1);

	const handleClose = () => {
		setIsExpanded(false);
		// Reset gesture state when closing
		setScale(1);
	};

	const handleImageClick = (e: React.MouseEvent) => {
		// Prevent closing when clicking on the image itself (for gestures)
		e.stopPropagation();
	};

	const handleWheel = (e: React.WheelEvent) => {
		e.preventDefault();
		const delta = e.deltaY > 0 ? 0.9 : 1.1;
		const newScale = Math.max(0.5, Math.min(3, scale * delta));
		setScale(newScale);
	};

	return (
		<AnimatePresence>
			{!isExpanded && (
				<motion.img
					layoutId={id}
					key={'compact-image'}
					src={src}
					alt="Expandable Image"
					onClick={() => setIsExpanded(true)}
					style={{ borderRadius: 6, height: 70, width: 70, objectFit: 'cover' }}
				/>
			)}
			{isExpanded && (
				<motion.div
					key="overlay"
					style={{
						position: 'fixed',
						top: 0,
						left: 0,
						zIndex: 900,
						height: '100vh',
						width: '100vw',
						backgroundColor: 'rgba(0, 0, 0, 0.8)',
						display: 'flex',
						justifyContent: 'center',
						alignItems: 'center',
					}}
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					onClick={handleClose}
					exit={{ opacity: 0 }}
				>
					{onDelete && (
						<ActionIcon
							style={{ zIndex: 901 }}
							pos="absolute"
							top={90}
							right={10}
							onClick={onDelete}
							bg="red"
						>
							<IconTrash />
						</ActionIcon>
					)}
					<motion.img
						layoutId={id}
						key="expanded-image"
						style={{
							borderRadius: 6,
							width: '100%',
							objectFit: 'contain',
							scale: scale,
						}}
						src={src}
						alt="Expandable Image"
						onClick={handleImageClick}
						onWheel={handleWheel}
						drag
						dragSnapToOrigin
						dragElastic={0.2}
						onDoubleClick={() => {
							// Double click to reset zoom
							setScale(1);
						}}
						whileDrag={{ cursor: 'grabbing' }}
						whileHover={{ cursor: 'grab' }}
						transition={{
							type: 'spring',
							stiffness: 400,
							damping: 30,
						}}
					/>
				</motion.div>
			)}
		</AnimatePresence>
	);
};
